/**
 * modals/EntryModelPicker.tsx
 * Multi-select picker for entry models
 *
 * Shows available entry models in a flat list.
 * Allows multi-select and inline creation of new entry models.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { Plus, Target, MoreVertical, Pencil, Trash2 } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Listbox,
  ListboxItem,
  ListboxSection,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem
} from '@enfomentis/ui';

// 3. Relative imports
import type { EntryModelDraft, PlaybookDraft, EntryModelCategoryDraft } from '../hooks/usePlaybookBuilder';
import { EntryModelModal } from './EntryModelModal';
import { DeleteEntryModelConfirmationModal } from './DeleteEntryModelConfirmationModal';

/**
 * Props for EntryModelPicker
 */
interface EntryModelPickerProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Available entry models */
  entryModels: EntryModelDraft[];
  /** Entry model categories */
  entryModelCategories: EntryModelCategoryDraft[];
  /** Currently selected entry model IDs */
  selectedEntryModelIds: string[];
  /** All playbooks (for delete confirmation) */
  playbooks: PlaybookDraft[];
  /** Current playbook ID (for delete confirmation) */
  currentPlaybookId: string;
  /** Handler for selection change */
  onSelectionChange: (selectedIds: string[]) => void;
  /** Handler for adding new entry model */
  onAddEntryModel: (
    entryModelData: { name: string; description?: string; categoryId?: string },
    newCategory?: { name: string; description?: string; color: string }
  ) => string;
  /** Handler for creating a new category */
  onCreateCategory: (name: string, color: string, description?: string) => string;
  /** Handler for updating an entry model */
  onUpdateEntryModel: (tempId: string, updates: { name?: string; description?: string; categoryId?: string }) => void;
  /** Handler for removing an entry model */
  onRemoveEntryModel: (tempId: string) => void;
}

/**
 * Empty state component
 */
const EmptyState: React.FC<{ onAddNew: () => void }> = ({ onAddNew }) => (
  <div className="flex-1 flex items-center justify-center py-12">
    <div className="text-center max-w-md">
      <Target className="w-16 h-16 text-text-secondary mx-auto mb-4" />
      <h4 className="text-lg font-medium text-text-primary mb-2">No Entry Models Yet</h4>
      <p className="text-sm text-text-secondary mb-4">
        Create your first entry model to define execution triggers for your playbook.
      </p>
      <Button
        color="primary"
        startContent={<Plus className="w-4 h-4" />}
        onPress={onAddNew}
      >
        Create First Entry Model
      </Button>
    </div>
  </div>
);

/**
 * Entry model picker component
 */
export const EntryModelPicker: React.FC<EntryModelPickerProps> = ({
  isOpen,
  onClose,
  entryModels,
  entryModelCategories,
  selectedEntryModelIds,
  playbooks,
  currentPlaybookId,
  onSelectionChange,
  onAddEntryModel,
  onCreateCategory,
  onUpdateEntryModel,
  onRemoveEntryModel
}) => {
  const [localSelectedKeys, setLocalSelectedKeys] = useState<Set<string>>(
    new Set(selectedEntryModelIds)
  );
  const [showEntryModelModal, setShowEntryModelModal] = useState(false);
  const [editingEntryModel, setEditingEntryModel] = useState<EntryModelDraft | undefined>(undefined);
  const [deletingEntryModelId, setDeletingEntryModelId] = useState<string | undefined>(undefined);

  // Update local selection when prop changes
  useEffect(() => {
    setLocalSelectedKeys(new Set(selectedEntryModelIds));
  }, [selectedEntryModelIds, isOpen]);

  /**
   * Handle opening add entry model modal
   */
  const handleAddNew = (): void => {
    setEditingEntryModel(undefined);
    setShowEntryModelModal(true);
  };

  /**
   * Handle opening edit entry model modal
   */
  const handleEdit = (entryModel: EntryModelDraft): void => {
    setEditingEntryModel(entryModel);
    setShowEntryModelModal(true);
  };

  /**
   * Handle saving entry model
   */
  const handleSaveEntryModel = (
    entryModelData: { name: string; description?: string; categoryId?: string },
    newCategory?: { name: string; description?: string; color: string }
  ): string => {
    if (editingEntryModel) {
      // Update existing entry model
      onUpdateEntryModel(editingEntryModel.tempId, entryModelData);
      return editingEntryModel.tempId;
    } else {
      // Add new entry model
      const tempId = onAddEntryModel(entryModelData, newCategory);
      // Auto-select the newly created item
      const updatedSelection = new Set(localSelectedKeys);
      updatedSelection.add(tempId);
      setLocalSelectedKeys(updatedSelection);
      return tempId;
    }
  };

  /**
   * Handle opening delete confirmation
   */
  const handleDeleteClick = (tempId: string): void => {
    setDeletingEntryModelId(tempId);
  };

  /**
   * Handle apply selection
   */
  const handleApply = (): void => {
    onSelectionChange(Array.from(localSelectedKeys));
    onClose();
  };

  /**
   * Handle cancel
   */
  const handleCancel = (): void => {
    // Reset to original selection
    setLocalSelectedKeys(new Set(selectedEntryModelIds));
    onClose();
  };

  const hasItems = entryModels.length > 0;

  // Group entry models by category
  const sortedCategories = [...entryModelCategories].sort((a, b) => a.order - b.order);
  const uncategorizedModels = entryModels.filter(m => !m.categoryId);
  const modelsByCategory = sortedCategories.map(category => ({
    category,
    models: entryModels.filter(m => m.categoryId === category.tempId)
  })).filter(group => group.models.length > 0);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex gap-2 items-center">
                <Target className="w-5 h-5 text-primary" />
                <span>Select Entry Models</span>
              </ModalHeader>

              <ModalBody>
                {!hasItems ? (
                  <EmptyState onAddNew={handleAddNew} />
                ) : (
                  <Listbox
                    aria-label="Entry models"
                    selectionMode="multiple"
                    selectedKeys={localSelectedKeys}
                    onSelectionChange={(keys) => setLocalSelectedKeys(keys as Set<string>)}
                    variant="light"
                    classNames={{
                      base: "max-h-[400px] overflow-auto"
                    }}
                  >
                    {[
                      // Render categorized entry models
                      ...modelsByCategory.map(({ category, models }) => (
                        <ListboxSection
                          key={category.tempId}
                          title={category.name}
                          classNames={{
                            heading: "text-xs font-semibold text-text-secondary uppercase tracking-wider px-2 py-1",
                            group: "mb-2"
                          }}
                        >
                        {models.map((entryModel) => (
                          <ListboxItem
                            key={entryModel.tempId}
                            textValue={entryModel.name}
                            classNames={{
                              base: "py-2",
                              selectedIcon: "text-primary scale-150 order-first"
                            }}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1">
                                <div className="font-medium text-text-primary">
                                  {entryModel.name}
                                </div>
                                {entryModel.description && (
                                  <div className="text-xs text-text-secondary mt-0.5">
                                    {entryModel.description}
                                  </div>
                                )}
                              </div>
                              <div onClick={(e) => e.stopPropagation()}>
                                <Dropdown>
                                  <DropdownTrigger>
                                    <Button
                                      isIconOnly
                                      size="sm"
                                      variant="light"
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownTrigger>
                                  <DropdownMenu
                                    aria-label="Entry model actions"
                                    onAction={(key) => {
                                      if (key === 'edit') {
                                        handleEdit(entryModel);
                                      } else if (key === 'delete') {
                                        handleDeleteClick(entryModel.tempId);
                                      }
                                    }}
                                  >
                                    <DropdownItem key="edit" startContent={<Pencil className="w-4 h-4" />}>
                                      Edit
                                    </DropdownItem>
                                    <DropdownItem
                                      key="delete"
                                      color="danger"
                                      startContent={<Trash2 className="w-4 h-4" />}
                                    >
                                      Delete
                                    </DropdownItem>
                                  </DropdownMenu>
                                </Dropdown>
                              </div>
                            </div>
                          </ListboxItem>
                        ))}
                      </ListboxSection>
                      )),

                      // Render uncategorized entry models
                      ...(uncategorizedModels.length > 0
                        ? [
                            <ListboxSection
                              key="uncategorized"
                              title="Uncategorized"
                              classNames={{
                                heading: "text-xs font-semibold text-text-secondary uppercase tracking-wider px-2 py-1",
                                group: "mb-2"
                              }}
                            >
                        {uncategorizedModels.map((entryModel) => (
                          <ListboxItem
                            key={entryModel.tempId}
                            textValue={entryModel.name}
                            classNames={{
                              base: "py-2",
                              selectedIcon: "text-primary scale-150 order-first"
                            }}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1">
                                <div className="font-medium text-text-primary">
                                  {entryModel.name}
                                </div>
                                {entryModel.description && (
                                  <div className="text-xs text-text-secondary mt-0.5">
                                    {entryModel.description}
                                  </div>
                                )}
                              </div>
                              <div onClick={(e) => e.stopPropagation()}>
                                <Dropdown>
                                  <DropdownTrigger>
                                    <Button
                                      isIconOnly
                                      size="sm"
                                      variant="light"
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownTrigger>
                                  <DropdownMenu
                                    aria-label="Entry model actions"
                                    onAction={(key) => {
                                      if (key === 'edit') {
                                        handleEdit(entryModel);
                                      } else if (key === 'delete') {
                                        handleDeleteClick(entryModel.tempId);
                                      }
                                    }}
                                  >
                                    <DropdownItem key="edit" startContent={<Pencil className="w-4 h-4" />}>
                                      Edit
                                    </DropdownItem>
                                    <DropdownItem
                                      key="delete"
                                      color="danger"
                                      startContent={<Trash2 className="w-4 h-4" />}
                                    >
                                      Delete
                                    </DropdownItem>
                                  </DropdownMenu>
                                </Dropdown>
                              </div>
                            </div>
                          </ListboxItem>
                        ))}
                            </ListboxSection>
                          ]
                        : [])
                    ]}
                  </Listbox>
                )}
              </ModalBody>

              <ModalFooter className="flex justify-between">
                <Button
                  variant="bordered"
                  color="primary"
                  startContent={<Plus className="w-4 h-4" />}
                  onPress={handleAddNew}
                >
                  Entry Model
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="light"
                    onPress={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onPress={handleApply}
                  >
                    Save
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Entry Model Modal */}
      <EntryModelModal
        isOpen={showEntryModelModal}
        mode={editingEntryModel ? 'edit' : 'add'}
        existingData={editingEntryModel}
        entryModelCategories={entryModelCategories}
        onClose={() => {
          setShowEntryModelModal(false);
          setEditingEntryModel(undefined);
        }}
        onSave={handleSaveEntryModel}
        onCreateCategory={onCreateCategory}
      />

      {/* Delete Confirmation Modal */}
      {(() => {
        if (!deletingEntryModelId) return null;

        const entryModel = entryModels.find(m => m.tempId === deletingEntryModelId);
        if (!entryModel) return null;

        return (
          <DeleteEntryModelConfirmationModal
            isOpen={true}
            entryModel={entryModel}
            currentPlaybookId={currentPlaybookId}
            playbooks={playbooks}
            isSelectedInCurrentPlaybook={localSelectedKeys.has(deletingEntryModelId)}
            onClose={() => setDeletingEntryModelId(undefined)}
            onUnselect={() => {
              // Remove from current playbook selection only
              const updatedSelection = new Set(localSelectedKeys);
              updatedSelection.delete(deletingEntryModelId);
              setLocalSelectedKeys(updatedSelection);
            }}
            onDelete={() => {
              // Remove entry model entirely
              onRemoveEntryModel(deletingEntryModelId);
              // Also remove from local selection if selected
              const updatedSelection = new Set(localSelectedKeys);
              updatedSelection.delete(deletingEntryModelId);
              setLocalSelectedKeys(updatedSelection);
            }}
          />
        );
      })()}
    </>
  );
};
